import React, { useRef, useState, useCallback, useEffect } from "react";

import { Box, SxProps } from "@mui/material";

import { RichTextEditorRef } from "mui-tiptap";

import EditorMenuControls from "@/components/Notes/EditorMenuControls";
import { StyledRichTextEditor } from "@/components/Notes/styles";
import useExtensions from "@/components/Notes/useExtensions";
import { htmlContentStyles } from "@/utils/htmlContentStyle";

interface ResizableRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  initialHeight?: number;
  hideMenuBar?: boolean;
  sx?: SxProps;
}

const ResizableRichTextEditor = ({
  value,
  onChange,
  placeholder,
  initialHeight = 200,
  hideMenuBar = false,
  sx,
}: ResizableRichTextEditorProps) => {
  const rteRef = useRef<RichTextEditorRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = useState(initialHeight);
  const [isDragging, setIsDragging] = useState(false);
  const extensions = useExtensions({
    placeholder: placeholder,
  });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newHeight = Math.max(
        hideMenuBar ? 65 : 125,
        Math.min(window.innerHeight - 200, e.clientY - containerRect.top)
      );
      setEditorHeight(newHeight);
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add and remove event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ns-resize";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `${editorHeight}px`,
        maxHeight: "calc(100vh - 200px)",
        pt: 2,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        ...sx,
      }}
    >
      <StyledRichTextEditor
        sx={{
          ...htmlContentStyles,
        }}
        ref={rteRef}
        extensions={extensions}
        content={value}
        renderControls={() => <EditorMenuControls showUndoRedo={false} />}
        RichTextFieldProps={{
          variant: "outlined",
          MenuBarProps: {
            disableSticky: true,
            hide: hideMenuBar,
          },
        }}
        onUpdate={({ editor }) => onChange(editor.getHTML())}
      />
      {/* Corner resize handle */}
      <Box
        sx={{
          position: "absolute",
          bottom: "2px",
          right: "2px",
          width: "12px",
          height: "12px",
          cursor: "nw-resize",
          background:
            "linear-gradient(-45deg, transparent 30%, rgba(0,0,0,0.1) 30%, rgba(0,0,0,0.1) 40%, transparent 40%, transparent 60%, rgba(0,0,0,0.1) 60%, rgba(0,0,0,0.1) 70%, transparent 70%)",
          "&:hover": {
            background:
              "linear-gradient(-45deg, transparent 30%, rgba(0,0,0,0.2) 30%, rgba(0,0,0,0.2) 40%, transparent 40%, transparent 60%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.2) 70%, transparent 70%)",
          },
        }}
        onMouseDown={handleMouseDown}
      />
    </Box>
  );
};

export default ResizableRichTextEditor;
